import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, useTheme } from '@mui/material';
import { isIOS, isMobile, isSafari } from 'react-device-detect';
import { FilePdf } from '@phosphor-icons/react';
import { saveAs } from 'file-saver';

import { DividerStyled } from 'enrollment/features/ProfileEdit/Avatar/AvatarStyled';
import { DocumentRequestDateIcon } from 'enrollment/assets/DocumentRequestDateIcon';
import {
  UploadDocumentsDrawerFooterStyled,
  UploadDocumentsDrawerInfoStyled,
  UploadDocumentsDrawerStyled,
} from '../UploadDocumentsDrawer/UploadDocumentsDrawerStyled';
import { CustomizedTimeline } from './components/Timeline';
import {
  ViewDocumentDetailsDrawerCardStyled,
  ViewDocumentDetailsDrawerStyled,
} from './ViewDocumentDetailsDrawerStyled';
import { DocumentPreviewDrawer } from '../DocumentPreviewDrawer';
import { base64ToBlob } from 'enrollment/utils/FileUtils/base64blob';
import { useSingleDocumentRequestQuery } from 'enrollment/queries';

export interface ViewDocumentDetailsDrawerProps {
  documentRequestId: number;
  beneficiaryName?: string;
  docId?: number;
  electedBenefits?: string;
  terminateDate?: string;
}

export const ViewDocumentDetailsDrawer = ({
  documentRequestId,
  beneficiaryName,
  docId,
  electedBenefits,
  terminateDate,
}: ViewDocumentDetailsDrawerProps) => {
  const { t } = useTranslation('pages.addYourDocuments');
  const theme = useTheme();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [openPreviewDrawer, setOpenPreviewDrawer] = useState(false);

  const { documentRequestsData } = useSingleDocumentRequestQuery({
    documentRequestId,
    enabled: openDrawer,
  });

  let index =
    documentRequestsData?.data?.submitted_documents.findIndex(
      (document) => document.id === docId
    ) || 0;
  index = index < 0 ? 0 : index;

  const contentType =
    documentRequestsData?.data?.submitted_documents[index]?.content_type;
  const fileData =
    documentRequestsData?.data?.submitted_documents[index]?.file_data;
  const fileName =
    documentRequestsData?.data?.submitted_documents[index]?.file_name;

  const handleDownload = () => {
    const blob = base64ToBlob(fileData);
    saveAs(blob as any, fileName);
  };

  return (
    <>
      <ps-button
        level="tertiary"
        id={`view-details-button-${documentRequestId}`}
        label={t('common:viewDetails')}
        show-underline
        data-testid="view-details-doc-req-btn"
        onClick={() => setOpenDrawer(true)}
      />

      <UploadDocumentsDrawerStyled
        open={openDrawer}
        anchor="right"
        id={`view-details-drawer-${documentRequestId}`}
        data-testid={`view-details-drawer-${documentRequestId}`}
        onClose={() => setOpenDrawer(false)}
      >
        <ps-button
          id={`back-button-${documentRequestId}`}
          label={isMobile ? t('common:back') : t('common:close')}
          level="tertiary"
          icon={isMobile ? 'caret-left' : 'x'}
          onClick={() => setOpenDrawer(false)}
          extraClass="p-0 my-3"
        />

        <Typography variant="h5" gutterBottom>
          {t('viewDetailsDrawerTitle', { subscriberName: beneficiaryName })}
        </Typography>
        <DividerStyled />
        <UploadDocumentsDrawerInfoStyled container>
          <Grid item>
            {electedBenefits && (
              <Typography variant="body2" gutterBottom>
                {t('electedBenefit') + `: ${electedBenefits}`}
              </Typography>
            )}
          </Grid>
          <Grid item>
            {terminateDate !== 'Invalid date' && (
              <>
                <Typography
                  variant="overline"
                  gutterBottom
                  sx={{ marginRight: '1rem' }}
                  dangerouslySetInnerHTML={{
                    __html: DocumentRequestDateIcon,
                  }}
                />
                <Typography variant="overline" gutterBottom>
                  {t('pendingBy', { pendingBy: terminateDate })}
                </Typography>
              </>
            )}
          </Grid>
        </UploadDocumentsDrawerInfoStyled>

        <>
          <>
            <ViewDocumentDetailsDrawerStyled>
              <Typography>
                {documentRequestsData?.data?.submitted_documents &&
                  documentRequestsData?.data?.submitted_documents?.length > 0 &&
                  documentRequestsData?.data?.submitted_documents[0]?.name}
              </Typography>
              <hr />
              <div className="document-details-card">
                <ViewDocumentDetailsDrawerCardStyled className="d-flex">
                  <div
                    className={`card-div d-flex align-items-center ${
                      contentType?.includes('/pdf') && (isIOS || isSafari)
                        ? 'isPdf'
                        : ''
                    }`}
                  >
                    {contentType?.includes('image/') ? (
                      <img
                        src={`data:${contentType};base64,${fileData}`}
                        alt="document_image"
                        onClick={() => setOpenPreviewDrawer(true)}
                      />
                    ) : (
                      <>
                        {isIOS || isSafari ? (
                          <a
                            className="d-flex flex-column justify-content-end text-decoration-none"
                            href={`data:${contentType};base64,${fileData}`}
                            download={fileName}
                          >
                            <FilePdf
                              size={80}
                              weight="thin"
                              fill={theme.palette.grey['300']}
                              className="pdf-icon"
                              onClick={handleDownload}
                            />
                            <Typography variant="body2" gutterBottom>
                              {t('pdfDownloadTitle')}
                            </Typography>
                          </a>
                        ) : (
                          <FilePdf
                            size={100}
                            weight="thin"
                            fill={theme.palette.grey['300']}
                            onClick={() => setOpenPreviewDrawer(true)}
                            className="pdf-icon"
                          />
                        )}
                      </>
                    )}
                  </div>
                </ViewDocumentDetailsDrawerCardStyled>
                <div className="timeline">
                  <CustomizedTimeline
                    data={
                      documentRequestsData?.data?.submitted_documents &&
                      documentRequestsData?.data?.submitted_documents?.length >
                        0 &&
                      documentRequestsData?.data?.submitted_documents[index]
                    }
                  />
                </div>
              </div>
            </ViewDocumentDetailsDrawerStyled>
          </>
          <UploadDocumentsDrawerFooterStyled container>
            <Grid marginX="2.4rem" item>
              <ps-button
                data-testid="add-beneficiary-cancel-btn"
                id="add-beneficiary-cancel-btn"
                level="tertiary"
                label={t('common:cancel')}
                onClick={() => setOpenDrawer(false)}
              />
            </Grid>
          </UploadDocumentsDrawerFooterStyled>
          <DocumentPreviewDrawer
            openDrawer={openPreviewDrawer}
            setOpenDrawer={setOpenPreviewDrawer}
            documentRequestId={documentRequestId}
            contentType={contentType}
            fileData={fileData}
          />
        </>
      </UploadDocumentsDrawerStyled>
    </>
  );
};
